import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, FileTextIcon, FolderSearchIcon, LayoutGridIcon, LightbulbIcon, PaletteIcon, PencilRulerIcon, ScissorsIcon, ShuffleIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
export const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/uQXYLw8zZJaSMVkYJfYRLc/0939a34e-f34e-4a6d-b780-4e1de598f781.webp';
export const heroGuarantees = ['Download digitale immediato', 'Pagamento unico', 'Nessun abbonamento', 'Garanzia soddisfatti o rimborsati di 7 giorni'];
export const stats = [{
  value: '500+',
  label: 'Pagine di risorse'
}, {
  value: '8',
  label: 'Volumi digitali di Arte'
}, {
  value: '8',
  label: 'Classi — dalla 1ª Primaria alla 3ª Media'
}, {
  value: '5',
  label: 'Bonus inclusi'
}, {
  value: '1',
  label: 'Sistema didattico completo'
}];
export const differenceStats = [{
  value: 'Dalla 1ª Primaria alla 3ª Media',
  label: 'Materiale organizzato per tutto il percorso scolastico'
}, {
  value: '8 volumi digitali',
  label: "Dalla scoperta di colori, linee e forme fino alle avanguardie, all'arte contemporanea e alla preparazione per l'esame."
}, {
  value: 'PDF pronti per la stampa',
  label: 'Da utilizzare digitalmente o stampare direttamente per la classe.'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Le domeniche spariscono',
  text: 'Ore davanti al computer a cercare idee e preparare le lezioni della settimana successiva — e non hai ancora finito.'
}, {
  title: 'Ore passate a cercare materiali',
  text: 'Pinterest, Google, vecchie schede e materiali trovati online che spesso devono essere adattati prima di poter essere utilizzati.'
}, {
  title: 'Le attività vengono create da zero',
  text: "Consegne, esempi, immagini, materiali e impaginazione: ciò che dovrebbe richiedere pochi minuti diventa un'altra lunga attività da preparare."
}, {
  title: 'È difficile rendere Arte davvero visuale',
  text: 'Colori, prospettiva, composizione, texture, artisti e tecniche richiedono esempi chiari e materiali visuali ben costruiti.'
}, {
  title: 'Verifiche e valutazioni richiedono altro tempo',
  text: "Preparare attività di controllo, criteri di valutazione e materiali per verificare l'apprendimento significa ancora più lavoro fuori dall'orario scolastico."
}, {
  title: 'Stress prima della prossima lezione',
  text: "Quando il tempo non basta, è facile ritrovarsi a improvvisare o mettere insieme materiali all'ultimo minuto."
}];
export const afterItems = ['Un percorso di Arte organizzato dalla Primaria alla Scuola Media', 'Lezioni visuali semplici da utilizzare', 'Schede operative già pronte', 'Laboratori creativi', 'Attività pratiche', 'Esercizi di osservazione e analisi', 'Materiali per colori, forme, tecniche e prospettiva', "Storia dell'arte resa visuale e operativa", 'Verifiche pronte', "Supporto per organizzare l'intero anno"];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Le domeniche sono dedicate alla pianificazione',
  text: 'Passi parte del fine settimana a cercare o creare lezioni di Arte per la settimana successiva?'
}, {
  icon: FolderSearchIcon,
  title: 'Non sai quale attività utilizzare',
  text: 'Cerchi continuamente idee, schede o laboratori adatti alla classe che stai insegnando?'
}, {
  icon: LightbulbIcon,
  title: 'Vorresti spiegazioni più visuali',
  text: "Hai bisogno di materiali più chiari per spiegare colore, prospettiva, composizione, tecniche e storia dell'arte?"
}, {
  icon: PencilRulerIcon,
  title: 'Vuoi smettere di creare tutto da zero',
  text: 'Vorresti che lezioni, schede, attività e laboratori fossero già preparati?'
}, {
  icon: ShuffleIcon,
  title: 'Gli studenti hanno bisogno di più varietà',
  text: 'Vorresti utilizzare più giochi visuali, sfide creative, progetti e attività pratiche?'
}, {
  icon: LayoutGridIcon,
  title: 'Hai bisogno di una struttura più chiara',
  text: 'Vorresti avere tutto il materiale organizzato in modo da trovare rapidamente ciò che ti serve?'
}];

// Spine colours mirror the physical box (pink, orange, yellow, green, cyan, blue, violet, purple).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro?: string;
  items: string[];
  outro?: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Volume 01',
  level: '1ª Primaria',
  title: 'Il Risveglio Creativo',
  tagline: "Dai agli alunni una base semplice e coinvolgente nel linguaggio visuale.",
  intro: "Il primo volume introduce i bambini al mondo dell'Arte attraverso attività semplici, visuali e operative.",
  items: ['Colori primari e secondari', 'Linee e segni', 'Forme geometriche', 'Colore ed emozioni', 'Texture', 'Frottage', 'Attività creative', 'Schede da stampare', 'Giochi visuali', 'Prime attività ispirate ai grandi artisti', 'Ripasso e verifica'],
  outro: 'Materiale già pronto che rende i primi concetti artistici concreti, visuali e facili da insegnare.',
  spine: 'bg-pink-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 02',
  level: '2ª Primaria',
  title: 'Texture e Profondità',
  tagline: 'Texture, forme, spazio, collage, superfici e tridimensionalità.',
  items: ['Texture', 'Forme', 'Spazio', 'Collage', 'Superfici', 'Tridimensionalità'],
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volume 03',
  level: '3ª Primaria',
  title: 'Viaggio nel Tempo',
  tagline: 'Dalla Preistoria al mondo antico attraverso schede e laboratori pratici.',
  items: ['Preistoria', 'Mondo antico', 'Schede operative', 'Laboratori pratici'],
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volume 04',
  level: '4ª Primaria',
  title: 'La Tecnica Prende Forma',
  tagline: 'Luce, ombra, volume, tecniche grafiche e introduzione alla prospettiva.',
  items: ['Luce', 'Ombra', 'Volume', 'Tecniche grafiche', 'Introduzione alla prospettiva'],
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 05',
  level: '5ª Primaria',
  title: 'Verso il Moderno',
  tagline: 'Artisti, colore, avanguardie, collage, espressione e creatività moderna.',
  items: ['Artisti', 'Colore', 'Avanguardie', 'Collage', 'Espressione', 'Creatività moderna'],
  spine: 'bg-cyan-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volume 06',
  level: '1ª Media',
  title: 'Linguaggio Visivo',
  tagline: "Composizione, geometria dell'arte, colore, spazio e Impressionismo.",
  items: ['Composizione', "Geometria dell'arte", 'Colore', 'Spazio', 'Impressionismo'],
  spine: 'bg-blue-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 07',
  level: '2ª Media',
  title: 'Rinascimento e Barocco',
  tagline: 'Prospettiva, grandi maestri, luce, composizione e schede tecniche avanzate.',
  items: ['Prospettiva', 'Grandi maestri', 'Luce', 'Composizione', 'Schede tecniche avanzate'],
  spine: 'bg-violet-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 08',
  level: '3ª Media',
  title: 'Il Novecento e Oltre',
  tagline: "Dal Cubismo alla Street Art, arte contemporanea e preparazione all'esame finale.",
  items: ['Cubismo', 'Street Art', 'Arte contemporanea', "Preparazione all'esame finale"],
  spine: 'bg-purple-700 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '8 volumi digitali dalla 1ª Primaria alla 3ª Media',
  text: "Un percorso completo che ti aiuta a organizzare e svolgere le lezioni di Arte durante tutto l'anno scolastico. Ogni volume contiene 50 pagine di materiali, schede operative, attività e laboratori."
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Lezioni già pronte',
  text: 'Non partire più da un foglio bianco. Spiegazioni visuali, esempi e attività ti aiutano a preparare la lezione molto più velocemente.'
}, {
  icon: FileTextIcon,
  title: 'Schede operative stampabili',
  text: 'Attività che puoi stampare e utilizzare immediatamente per esercitazioni individuali, laboratori, ripasso e consolidamento.'
}, {
  icon: PaletteIcon,
  title: 'Lezioni visuali',
  text: "Colori, forme, prospettiva, texture, composizione e opere d'arte diventano più semplici grazie a materiali visuali chiari."
}, {
  icon: ScissorsIcon,
  title: 'Laboratori e attività creative',
  text: 'Collage, frottage, prospettiva, pittura, tecniche grafiche, attività ispirate agli artisti e progetti creativi pronti da utilizzare.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Verifiche',
  text: 'Materiali pronti per verificare la comprensione degli argomenti e seguire i progressi degli studenti.'
}, {
  icon: CalendarCheckIcon,
  title: 'Percorso annuale',
  text: "Un sistema organizzato dalla 1ª Primaria alla 3ª Media che ti aiuta a scegliere gli argomenti e mantenere una progressione chiara durante tutto l'anno."
}];
export const pageSamples: Array<{
  src: string;
  label: string;
  title: string;
  text: string;
}> = [{
  src: 'https://cdn.magicpatterns.com/uploads/ssjU3CHFpiuigEh4aTS4tS/Captura-de-pantalla-2026-10-15-a-la_s_-10.58.04_p._m..webp',
  label: 'Lezione Visuale · Volume 01',
  title: 'Una linea può andare ovunque!',
  text: 'Linea dritta, curva, spezzata, ondulata e spirale: gli alunni osservano ogni tipo di linea e la ripassano con colori diversi.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/eejSt67AHehXWniXSszKff/Captura-de-pantalla-2026-10-15-a-la_s_-10.58.42_p._m..webp',
  label: 'Scheda Alunno · Volume 02',
  title: 'Punti, segni e tracce',
  text: 'Puntini, trattini, croci e cerchietti diventano superfici: gli alunni riempiono ogni forma con una texture diversa.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/fLfALcagQdW7A3krZpCmkZ/Captura-de-pantalla-2026-10-15-a-la_s_-10.59.18_p._m..webp',
  label: 'Laboratorio · Volume 03',
  title: 'Crea una parete egizia',
  text: 'Un esempio di composizione, una checklist e la palette egizia guidano gli alunni nella creazione della loro parete decorata.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/4bGSUVNJ92cgpzmAAxgsLw/Captura-de-pantalla-2026-10-15-a-la_s_-11.00.03_p._m..webp',
  label: 'Esercizio di Osservazione · Volume 06',
  title: 'Quando la matematica diventa immagine',
  text: 'Gli studenti cercano cerchi, triangoli, assi di simmetria, ripetizioni e punto focale in una composizione geometrica.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/h6YiweDqR9ABSN2J3ZrEho/Captura-de-pantalla-2026-10-15-a-la_s_-11.00.47_p._m..webp',
  label: 'Composizione Piramidale · Volume 07',
  title: "Un triangolo nascosto nell'opera",
  text: 'Vertice, lati e base: gli studenti scoprono e tracciano la struttura triangolare nelle composizioni dei grandi maestri.'
}];
export const pageTypes = ['Lezioni Visuali', 'Schede Operative', 'Laboratori Creativi', 'Verifiche'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Registro Voti & Valutazione Rapida',
  text: 'Griglie e rubriche già pronte per rendere la valutazione più semplice e veloce.',
  items: ['Griglie di valutazione', 'Criteri chiari', 'Registro dei risultati', 'Schede di osservazione', 'Valutazione più rapida'],
  benefit: 'Meno tempo sulla burocrazia e più tempo per insegnare.'
}, {
  title: 'Kit Inclusione DSA, BES e DVA',
  text: 'Materiali e indicazioni per rendere le attività più accessibili e adattabili alle diverse esigenze presenti in classe.',
  items: ['Adattamenti visuali', 'Consegne semplificate', 'Supporti didattici', 'Strategie inclusive', 'Attività adattabili'],
  benefit: 'Più strumenti per permettere a tutti di partecipare.'
}, {
  title: 'Manuale LIM & Didattica Digitale',
  text: 'Una guida pratica per utilizzare il materiale di ARTE PRONTA™ anche digitalmente.',
  items: ['Utilizzo sulla LIM', 'Lezioni visuali', 'Presentazione delle opere', 'Attività digitali', 'Organizzazione della lezione'],
  benefit: 'Trasforma la tua aula in un piccolo museo visuale.'
}, {
  title: 'Banca del Tempo: 100 Attività da 10 Minuti',
  text: 'Una raccolta di attività rapide per quei momenti in cui ti serve immediatamente qualcosa da fare.',
  items: ['Attività creative', 'Mini sfide', 'Disegno', 'Osservazione', 'Colore e composizione', 'Attività di fine lezione'],
  benefit: 'Perfetto quando ti servono idee pronte senza dover creare nulla.'
}, {
  title: 'Kit “Profe-Genitori” Sereno',
  text: 'Materiali per comunicare meglio il valore del percorso artistico alle famiglie.',
  items: ['Comunicazioni pronte', 'Progressi degli studenti', 'Obiettivi didattici', 'Presentazione dei progetti', 'Supporto alla comunicazione scuola-famiglia'],
  benefit: 'Più chiarezza, meno stress nella comunicazione.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Apri ARTE PRONTA™',
  text: 'Scarica il pacchetto e apri il volume corrispondente alla tua classe.'
}, {
  title: 'Scegli la lezione e il materiale',
  text: "Trova l'argomento e scegli la scheda, il laboratorio o l'attività più adatta ai tuoi studenti."
}, {
  title: 'Preparati in 5 minuti',
  text: 'Leggi la consegna, guarda gli esempi visuali e scegli il materiale necessario.'
}, {
  title: 'Stampa e insegna',
  text: 'Stampa le schede oppure utilizza il materiale digitalmente — ed entra in classe con un piano chiaro.'
}];
export const audienceItems = ['Insegni Arte nella Scuola Primaria o Secondaria di Primo Grado', 'Vuoi un intero percorso di Arte già organizzato', 'Vuoi smettere di creare ogni scheda da zero', 'Hai bisogno di più materiali visuali', 'Vuoi utilizzare laboratori e attività pratiche', 'Vuoi avere verifiche già pronte', 'Vuoi risparmiare tempo nella pianificazione settimanale', 'Vuoi sentirti più preparato prima di ogni lezione'];
export const timeValueTasks = ['Cercare idee online', 'Cercare schede', 'Preparare spiegazioni visuali', 'Creare laboratori', 'Cercare opere e artisti', 'Preparare verifiche', 'Organizzare la programmazione'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '8 volumi digitali',
  detail: 'Dalla 1ª Primaria alla 3ª Media'
}, {
  label: '500+ pagine di risorse',
  detail: "Colori, forme, texture, prospettiva, tecniche, artisti, storia dell'arte, avanguardie e arte contemporanea."
}, {
  label: '5 Bonus senza costi aggiuntivi',
  detail: 'Valutazione, inclusione, LIM, 100 attività rapide e comunicazione con le famiglie.'
}, {
  label: 'Per Primaria e Scuola Media',
  detail: 'Lezioni, schede, laboratori e verifiche già pronti'
}];
export const offerIncludes = ['Download digitale immediato', 'PDF da salvare', 'Schede stampabili', 'Nessun prodotto fisico verrà spedito'];
export const PRICE = '19\u00A0€';

// Only add authentic reviews of ARTE PRONTA. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Come ricevo il materiale?',
  a: "ARTE PRONTA™ viene consegnato digitalmente. Dopo l'acquisto ricevi accesso ai file PDF che puoi scaricare e salvare. Puoi utilizzarli su computer, tablet o smartphone e stampare le schede di cui hai bisogno."
}, {
  q: 'ARTE PRONTA™ è adatto alla scuola italiana?',
  a: "Sì. Il materiale è stato organizzato per un percorso dalla 1ª Primaria alla 3ª Media, con attività, linguaggio e progressione pensati per l'utilizzo didattico nelle classi italiane."
}, {
  q: 'È adatto dalla 1ª Primaria alla 3ª Media?',
  a: 'Sì. Il pacchetto contiene 8 volumi differenti, uno per ciascun anno scolastico del percorso.'
}, {
  q: 'Posso davvero preparare una lezione in 5 minuti?',
  a: "ARTE PRONTA™ è progettato per ridurre drasticamente il tempo necessario alla preparazione. Apri il volume, scegli l'argomento, controlla la scheda o il laboratorio e prepara i materiali necessari."
}, {
  q: 'Il materiale copre davvero un intero anno?',
  a: "Il pacchetto è progettato come un percorso completo da utilizzare durante l'anno scolastico, con lezioni, attività, laboratori, progetti e verifiche organizzati progressivamente."
}, {
  q: 'Ci sono schede già pronte?',
  a: 'Sì. Il pacchetto contiene numerose schede operative già progettate per essere utilizzate direttamente in classe.'
}, {
  q: 'Ci sono verifiche?',
  a: 'Sì. Nei volumi sono presenti attività di ripasso e verifica per controllare la comprensione degli argomenti.'
}, {
  q: 'Posso utilizzarlo con studenti di livelli diversi?',
  a: 'Molte attività possono essere adattate, semplificate o approfondite in base alla classe e alle esigenze degli studenti. Inoltre è incluso il Kit Inclusione DSA, BES e DVA.'
}, {
  q: 'Devo stampare tutto?',
  a: 'No. Puoi stampare soltanto le schede necessarie e utilizzare molte delle lezioni visuali direttamente su computer, tablet o LIM.'
}, {
  q: 'Sono inclusi i 5 Bonus?',
  a: 'Sì. Ricevi tutti e 5 i Bonus insieme agli 8 volumi senza costi aggiuntivi.'
}, {
  q: 'È un abbonamento?',
  a: `No. Paghi ${PRICE} una sola volta. Nessun abbonamento.`
}, {
  q: 'Posso salvare i file?',
  a: 'Sì. I materiali vengono forniti digitalmente e possono essere scaricati e salvati.'
}, {
  q: 'Posso utilizzare ARTE PRONTA™ su tablet?',
  a: 'Sì. Puoi visualizzare i PDF su computer, tablet e smartphone, oltre a utilizzarli sulla LIM.'
}, {
  q: "C'è una garanzia?",
  a: 'Sì. Hai una garanzia soddisfatti o rimborsati di 7 giorni, secondo le condizioni indicate.'
}];
